"use client";

import { BookDemoButton } from "@/components/blocks/book-demo/button";
import { Button } from "@/components/ui/button";
import { NAVIGATION } from "@/lib/constants/navigation";
import { cn } from "@/lib/utils/tw";
import { PanelRightClose, PanelRightOpen, LucideArrowLeft } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Logo } from "./Logo";
// import { DotLottieReact } from "@lottiefiles/dotlottie-react";

type MobileMenuProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

// Add new MobileMenu component within the same file
const MobileMenu = ({ isOpen, setIsOpen }: MobileMenuProps) => {
  // Add effect to toggle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const pathname = usePathname();

  return (
    <div
      className={cn(
        "fixed inset-0 z-40 flex h-full flex-col bg-background pt-14 transition-transform duration-300 ease-in-out md:hidden",
        isOpen ? "translate-x-0" : "translate-x-full",
      )}
    >
      <div className="flex h-full w-full flex-1 px-1.5">
        <div className="flex h-full w-full flex-1 flex-col border-x">
          {/* Main menu content */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="mx-auto flex max-w-screen-xl flex-col space-y-4">
              <Link
                href={NAVIGATION.SECURITY.href}
                className="pointer-events-auto"
              >
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 text-lg",
                    pathname === NAVIGATION.SECURITY.href && "bg-secondary",
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  <p>{NAVIGATION.SECURITY.label}</p>
                </Button>
              </Link>
              {/* Commented out customers link
              <Link
                href={NAVIGATION.CUSTOMERS.href}
                className="pointer-events-auto"
              >
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 text-lg",
                    pathname === NAVIGATION.CUSTOMERS.href && "bg-secondary",
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  <p>{NAVIGATION.CUSTOMERS.label}</p>
                </Button>
              </Link>
              */}
              <Link
                href={NAVIGATION.COMPANY.href}
                className="pointer-events-auto"
              >
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 text-lg",
                    pathname === NAVIGATION.COMPANY.href && "bg-secondary",
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  <p>{NAVIGATION.COMPANY.label}</p>
                </Button>
              </Link>
              {/* Commented out careers link
              <Link href={NAVIGATION.CAREERS.href}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-lg"
                >
                  <p>{NAVIGATION.CAREERS.label}</p>
                </Button>
              </Link>
              */}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col border-t px-1.5">
        {/* Footer */}
        <div className="border-x bg-white p-4">
          <div className="mx-auto flex max-w-screen-xl justify-between gap-4">
            <Link
              href={NAVIGATION.LOGIN.href}
              className="pointer-events-auto flex-1"
            >
              <Button
                variant="outline"
                className="h-9 w-full flex-1 justify-center text-lg"
                onClick={() => setIsOpen(false)}
              >
                <p>{NAVIGATION.LOGIN.label}</p>
              </Button>
            </Link>

            <Link
              href={NAVIGATION.CONTACT.href}
              className="pointer-events-auto flex-1"
            >
              <BookDemoButton
                className="h-9 w-full flex-1 justify-center text-lg"
                onClick={() => setIsOpen(false)}
              >
                <p>Book a Demo</p>
              </BookDemoButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

interface HeaderProps {
  dark?: boolean;
}

const Header = ({ dark = false }: HeaderProps) => {
  // Add mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  // List of paths that should use dark mode
  const darkModePaths = ["/security"];
  const isDarkMode = darkModePaths.includes(pathname) && !mobileMenuOpen;

  const isContactOrInvest = pathname === "/contact" || pathname === "/invest";

  // Add resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        // md breakpoint is 768px
        setMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <nav
        className={cn(
          "pointer-events-none fixed left-0 right-0 top-0 z-50 flex h-12 items-center border-b px-1.5",
          isDarkMode
            ? "border-border-dark bg-background-dark"
            : "bg-background",
        )}
      >
        <div
          className={cn(
            "mx-auto flex h-full w-full max-w-screen-xl items-center justify-between border-x px-3 py-2",
            isDarkMode && "border-border-dark",
          )}
        >
          {/* Left logo */}
          <Logo isDarkMode={isDarkMode} />

          {isContactOrInvest && (
            <div className="flex items-center">
              <Link href="/" className="pointer-events-auto">
                <Button variant="ghost" className="flex items-center gap-1">
                  <LucideArrowLeft className="size-icon" />
                  <span>Back Home</span>
                </Button>
              </Link>
            </div>
          )}

          {/* Desktop navigation - centered */}
          {!isContactOrInvest && (
            <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center md:flex">
              <div className="flex h-7 items-center gap-2">
                <Link
                  href={NAVIGATION.SECURITY.href}
                  className="pointer-events-auto"
                >
                  <Button
                    variant={"ghost"}
                    className={cn(
                      isDarkMode && "dark",
                      pathname === NAVIGATION.SECURITY.href && "bg-secondary",
                    )}
                  >
                    <p>{NAVIGATION.SECURITY.label}</p>
                  </Button>
                </Link>
                {/* Commented out customers link
                <Link
                  href={NAVIGATION.CUSTOMERS.href}
                  className="pointer-events-auto"
                >
                  <Button
                    variant={"ghost"}
                    className={cn(
                      isDarkMode && "dark",
                      pathname === NAVIGATION.CUSTOMERS.href && "bg-secondary",
                    )}
                  >
                    <p>{NAVIGATION.CUSTOMERS.label}</p>
                  </Button>
                </Link>
                */}
                <Link
                  href={NAVIGATION.COMPANY.href}
                  className="pointer-events-auto"
                >
                  <Button
                    variant={"ghost"}
                    className={cn(
                      isDarkMode && "dark",
                      pathname === NAVIGATION.COMPANY.href && "bg-secondary",
                    )}
                  >
                    <p>{NAVIGATION.COMPANY.label}</p>
                  </Button>
                </Link>
                {/* Commented out careers link
                <Link
                  href={NAVIGATION.CAREERS.href}
                  className="pointer-events-auto"
                >
                  <Button
                    variant={"ghost"}
                    className={cn(
                      isDarkMode && "dark",
                      pathname === NAVIGATION.CAREERS.href && "bg-secondary",
                    )}
                  >
                    <p>{NAVIGATION.CAREERS.label}</p>
                  </Button>
                </Link>
                */}
              </div>
            </div>
          )}

          {/* Right side buttons */}
          {!isContactOrInvest && (
            <div className="flex items-center space-x-4">
              <Link
                href={NAVIGATION.LOGIN.href}
                className="pointer-events-auto hidden md:block"
              >
                <Button
                  variant={"outline"}
                  className={cn(isDarkMode && "dark")}
                >
                  <p>{NAVIGATION.LOGIN.label}</p>
                </Button>
              </Link>

              {!mobileMenuOpen && (
                <Link
                  href={NAVIGATION.CONTACT.href}
                  className="pointer-events-auto"
                >
                  <BookDemoButton className={cn(isDarkMode && "dark")}>
                    <p>Book a Demo</p>
                  </BookDemoButton>
                </Link>
              )}

              {/* Mobile menu button */}
              <div className="flex items-center md:hidden">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                  className={cn("pointer-events-auto", isDarkMode && "dark")}
                >
                  {mobileMenuOpen ? (
                    <PanelRightClose className="size-icon-lg" />
                  ) : (
                    <PanelRightOpen className="size-icon-lg" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile menu component */}
      <MobileMenu isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} />
    </>
  );
};
export default Header;
